class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.closeHandler = this.close.bind(this);

    this.returnHandler = () => {
      if (document.visibilityState === 'visible') {
        this.refreshContents();
      }
    };

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    window.addEventListener(
      'focus',
      this.returnHandler
    );

    window.addEventListener(
      'pageshow',
      this.returnHandler
    );

    document.addEventListener(
      'visibilitychange',
      this.returnHandler
    );
  }

  disconnectedCallback() {
    window.removeEventListener(
      'focus',
      this.returnHandler
    );

    window.removeEventListener(
      'pageshow',
      this.returnHandler
    );

    document.removeEventListener(
      'visibilitychange',
      this.returnHandler
    );

    clearTimeout(this.openTimer);
  }

  bindOverlay() {
    this.querySelector('#CartDrawer-Overlay')
      ?.addEventListener(
        'click',
        this.closeHandler
      );
  }

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.getElementById('cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
      this.refreshContents();
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        this.open(cartLink);
        this.refreshContents();
      }
    });
  }

  refreshContents() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.dataset.crestCartRefreshing = 'true';
    this.setRefreshing(true);

    const operation = CartItems.mutationQueue
      .catch(() => {})
      .then(async () => {
        if (!this.isConnected) return false;

        const items = this.querySelector(
          'cart-drawer-items'
        );

        if (!items) return false;

        const refreshed = await items.onCartUpdate();

        if (
          refreshed === true &&
          typeof publish === 'function'
        ) {
          const cart = await items.readCurrentCart();

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            sourceElement: this.querySelector(
              'cart-drawer-items'
            ),
            cartData: cart,
          });
        }

        return refreshed;
      })
      .catch((error) => {
        console.warn(
          'Aggiornamento carrello non disponibile:',
          error
        );

        return false;
      })
      .finally(() => {
        delete this.dataset.crestCartRefreshing;
        this.setRefreshing(false);
        this.refreshPromise = null;
      });

    this.refreshPromise = operation;

    return operation;
  }

  setRefreshing(refreshing) {
    this.setAttribute(
      'aria-busy',
      String(refreshing)
    );

    const contents = this.querySelector(
      'cart-drawer-items'
    );

    if (contents) {
      contents.inert = refreshing;
    }

    const footer = this.querySelector(
      '.drawer__footer'
    );

    if (footer) {
      footer.inert = refreshing;
    }
  }

  showUpdateNotice(message) {
    const container = this.querySelector(
      '.drawer__inner'
    );

    if (!container) return;

    let notice = container.querySelector(
      '[data-crest-cart-notice]'
    );

    if (!notice) {
      notice = document.createElement('p');

      notice.dataset.crestCartNotice = '';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');

      notice.style.cssText =
        'padding:12px 16px;margin:12px 0;line-height:1.5;flex-shrink:0;';

      container.appendChild(notice);
    }

    notice.textContent = message;
  }

  syncEmptyState() {
    const empty =
      !!this.querySelector('.drawer__inner-empty') &&
      !this.querySelector('.cart-item');

    this.classList.toggle('is-empty', empty);

    return empty;
  }

  focusContents(preferred) {
    if (!this.classList.contains('active')) return;

    this.syncEmptyState();

    const container = this.querySelector(
      '.drawer__inner'
    );

    if (!container) return;

    const target =
      preferred?.isConnected &&
      container.contains(preferred)
        ? preferred
        : container.querySelector(
            '.drawer__close, a, button'
          ) || container;

    trapFocus(container, target);
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    this.syncEmptyState();

    if (this.classList.contains('active')) return;

    const note = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (note && !note.hasAttribute('role')) {
      this.setSummaryAccessibility(note);
    }

    clearTimeout(this.openTimer);

    this.openTimer = setTimeout(() => {
      if (!this.isConnected) return;

      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');

      this.focusContents();

      this.querySelector(
        'cart-drawer-items'
      )?.dispatchViewEvent();
    });
  }

  close() {
    clearTimeout(this.openTimer);

    const wasOpen = this.classList.contains('active');
    const pageX = window.scrollX;
    const pageY = window.scrollY;

    const returnTarget = this.activeElement?.isConnected
      ? this.activeElement
      : null;

    this.classList.remove('active');

    // Rilascia il focus senza lo scorrimento predefinito
    // verso l'icona del carrello nell'header.
    removeTrapFocus();

    document.body.classList.remove('overflow-hidden');

    if (wasOpen && returnTarget) {
      returnTarget.focus({ preventScroll: true });
    }

    // Ripristina la posizione se il browser l'ha modificata
    // durante lo sblocco della pagina o il ritorno del focus.
    if (
      window.scrollX !== pageX ||
      window.scrollY !== pageY
    ) {
      window.scrollTo({
        left: pageX,
        top: pageY,
        behavior: 'instant',
      });
    }
  }

  setSummaryAccessibility(note) {
    note.setAttribute('role', 'button');

    note.setAttribute(
      'aria-expanded',
      note.closest('details')?.open
        ? 'true'
        : 'false'
    );

    const contentId = note.nextElementSibling?.id;

    if (contentId) {
      note.setAttribute(
        'aria-controls',
        contentId
      );
    }

    note.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').open
      );
    });

    note.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  captureFocus() {
    const active = document.activeElement;

    if (!this.contains(active)) return null;

    const row = active.closest('.cart-item');

    return {
      key: row
        ?.querySelector('[data-quantity-line-key]')
        ?.dataset.quantityLineKey,
      name: active.getAttribute('name'),
      id: active.id,
      remove: !!active.closest('cart-remove-button'),
    };
  }

  restoreFocus(saved) {
    let target;

    if (saved?.key) {
      const input = Array.from(
        this.querySelectorAll(
          '[data-quantity-line-key]'
        )
      ).find(
        (element) =>
          element.dataset.quantityLineKey === saved.key
      );

      const row = input?.closest('.cart-item');

      target = saved.remove
        ? row?.querySelector(
            'cart-remove-button button'
          )
        : Array.from(
            row?.querySelectorAll('[name]') || []
          ).find(
            (element) =>
              element.getAttribute('name') === saved.name
          );
    } else if (saved?.id) {
      const element =
        document.getElementById(saved.id);

      if (element && this.contains(element)) {
        target = element;
      }
    }

    this.focusContents(target);
  }

  renderContents(parsedState, options = {}) {
    const sections = parsedState?.sections;

    if (
      !sections ||
      typeof sections['cart-drawer'] !== 'string'
    ) {
      throw new Error(
        'Il contenuto aggiornato del carrello non è disponibile.'
      );
    }

    const parsed = new DOMParser().parseFromString(
      sections['cart-drawer'],
      'text/html'
    );

    const source = parsed.querySelector('#CartDrawer');
    const target = this.querySelector('#CartDrawer');

    if (
      !source?.querySelector('.drawer__inner') ||
      !target
    ) {
      throw new Error(
        'Il contenuto aggiornato del carrello non è valido.'
      );
    }

    const savedFocus = this.captureFocus();

    if (options.invalidate !== false) {
      CartItems.invalidateCartView();
    }

    this.productId = parsedState.id;
    target.innerHTML = source.innerHTML;

    const iconHTML = sections['cart-icon-bubble'];

    const icon =
      document.getElementById('cart-icon-bubble');

    if (
      typeof iconHTML === 'string' &&
      icon
    ) {
      const iconSource = new DOMParser()
        .parseFromString(iconHTML, 'text/html')
        .querySelector('.shopify-section');

      if (iconSource) {
        icon.innerHTML = iconSource.innerHTML;
      }
    }

    this.syncEmptyState();
    this.bindOverlay();

    this.setRefreshing(
      this.dataset.crestCartRefreshing === 'true'
    );

    const note = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (note && !note.hasAttribute('role')) {
      this.setSummaryAccessibility(note);
    }

    if (this.classList.contains('active')) {
      this.restoreFocus(savedFocus);
    }
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    return (
      this.getSectionDOM(html, selector)?.innerHTML ||
      ''
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (typeof html !== 'string') return null;

    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);